import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";

// Justice — the AI Documentation Coach mascot.
export function Justice({ size = 96, className = "" }) {
  return (
    <img
      src="/justice.png"
      alt="Justice, the EvidenceKeeper Documentation Coach"
      width={size}
      height={size}
      className={`object-contain select-none ${className}`}
      draggable={false}
    />
  );
}

// Rotating AI status messages (mirror the real backend stages where possible).
export const JUSTICE_MESSAGES = [
  "Justice is reading your evidence…",
  "Extracting source-documented facts…",
  "Checking dates and metadata…",
  "Linking facts to their sources…",
  "Organizing the timeline…",
  "Comparing confirmed order provisions…",
  "Preparing your report…",
  "Running the final integrity check…",
];

// Maps a report export job's real stage -> a Justice-flavored message.
export const REPORT_STAGE_MESSAGES = {
  authorization: "Justice is verifying your authorization…",
  validation: "Checking dates and metadata…",
  sources: "Linking facts to their sources…",
  images: "Preparing your exhibits…",
  hashes: "Fingerprinting each original file…",
  chronology: "Organizing the timeline…",
  bates: "Numbering your exhibits…",
  pdf: "Preparing your report…",
  integrity: "Running the final integrity check…",
};

// AnimatedJustice — the mascot performing a gentle idle dance while AI is working.
// Respects the OS "Reduce Motion" setting: falls back to a static image.
export function AnimatedJustice({ size = 72, className = "", testid = "animated-justice" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div data-testid={testid} aria-hidden="true"><Justice size={size} className={className} /></div>;
  return (
    <div className="relative inline-block" style={{ width: size, height: size }} data-testid={testid} aria-hidden="true">
      <motion.div
        animate={{ x: [-3, 3, -3], y: [0, -4, 0], rotate: [-1.6, 1.6, -1.6] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{ width: size, height: size, transformOrigin: "50% 90%" }}
      >
        <Justice size={size} className={className} />
      </motion.div>
      <motion.span
        className="absolute -right-0.5 -top-0.5"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5], rotate: [0, 25, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 1.1, ease: "easeInOut" }}
      >
        <Sparkles style={{ color: "var(--ek-primary)", width: Math.max(12, size * 0.2), height: Math.max(12, size * 0.2) }} />
      </motion.span>
    </div>
  );
}

// JusticeThinking — animated Justice + a status message.
// Pass `message` to show a single line tied to the real job state, or `messages`
// (defaults to JUSTICE_MESSAGES) to rotate through contextual lines.
export function JusticeThinking({ size = 44, messages, message, interval = 2400, compact = false, className = "", testid = "justice-thinking" }) {
  const list = message ? [message] : (messages && messages.length ? messages : JUSTICE_MESSAGES);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (message || list.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), interval);
    return () => clearInterval(t);
  }, [message, list.length, interval]);
  const current = message || list[idx];
  return (
    <div
      className={`flex ${compact ? "flex-row items-center gap-3" : "flex-col items-center gap-3 text-center"} ${className}`}
      data-testid={testid}
      role="status"
      aria-live="polite"
    >
      <AnimatedJustice size={size} />
      <AnimatePresence mode="wait">
        <motion.span
          key={current}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3 }}
          className="text-sm"
          style={{ color: "var(--ek-text-secondary)" }}
        >
          {current}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

export function JusticeSpeech({ message, size = 120 }) {
  return (
    <div className="flex items-start gap-4" data-testid="justice-speech">
      <Justice size={size} className="shrink-0" />
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35 }}
        className="relative mt-3 border px-4 py-3 max-w-md"
        style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
      >
        <span
          className="absolute -left-2 top-4 h-3 w-3 rotate-45 border-l border-b"
          style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
        />
        <p className="text-sm leading-relaxed" style={{ color: "var(--ek-text-secondary)" }}>{message}</p>
      </motion.div>
    </div>
  );
}
